// src/components/Subscription/SubscriptionModal.js
import React, { useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Avatar,
  Box,
  Typography,
  Stack,
  CircularProgress,
  IconButton,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, Refresh as RefreshIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';

export default function SubscriptionModal({ 
  open, 
  onClose, 
  subscriptions, 
  onOpenReview, 
  onRefreshSubscriptions,
  refreshingSubscriptions = false 
}) {
  const theme = useTheme();
  
  // Use provided subscriptions or empty array
  const displaySubscriptions = subscriptions || [];
  
  // Count reviewed vs unreviewed
  const reviewedCount = displaySubscriptions.filter(sub => sub.reviewed).length;
  const unreviewedCount = displaySubscriptions.length - reviewedCount;

  const handleRefreshSubscriptions = () => {
    if (onRefreshSubscriptions) {
      onRefreshSubscriptions();
    }
  };

  const openReview = (creator) => {
    if (onOpenReview) {
      onOpenReview(creator);
    } else {
      console.log('Opening review for creator:', creator);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.background.paper, 1)})`,
        }
      }}
    >
      {/* Header with close button and stats */}
      <Box
        sx={{
          position: 'relative',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: 'white',
          px: 3,
          py: 3,
          borderRadius: '16px 16px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            ✨ Your Subscriptions
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'white',
              bgcolor: alpha('#fff', 0.1),
              '&:hover': {
                bgcolor: alpha('#fff', 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Stats chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`${displaySubscriptions.length} Total`}
            size="small"
            sx={{
              bgcolor: alpha('#fff', 0.2),
              color: 'white',
              fontWeight: 600,
              backdropFilter: 'blur(10px)',
            }}
          />
          <Chip
            label={`${reviewedCount} Reviewed`}
            size="small"
            icon={<CheckCircleIcon sx={{ color: 'white !important' }} />}
            sx={{
              bgcolor: alpha('#4caf50', 0.3),
              color: 'white',
              fontWeight: 600,
              backdropFilter: 'blur(10px)',
            }}
          />
          <Chip
            label={`${unreviewedCount} Pending`}
            size="small"
            sx={{
              bgcolor: alpha('#ff9800', 0.3),
              color: 'white',
              fontWeight: 600,
              backdropFilter: 'blur(10px)',
            }}
          />
        </Box>
      </Box>
      {/* Scrollable content */}
      <DialogContent 
        sx={{ 
          p: 0,
          maxHeight: '60vh',
          overflowY: 'auto',
          bgcolor: theme.palette.background.paper,
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: alpha(theme.palette.divider, 0.1),
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.primary.main, 0.3),
            borderRadius: '4px',
            '&:hover': {
              background: alpha(theme.palette.primary.main, 0.5),
            },
          },
        }}
      >
        {displaySubscriptions?.length ? (
          <Stack spacing={0}>
            {displaySubscriptions.map((sub, i) => {
              const creator = sub.creator || {};
              const name = creator.name || creator.username || 'Unknown Creator';
              const username = creator.username || 'unknown';
              const avatar = creator.profilePicture;
              
              return (
                <Box
                  key={i}
                  sx={{
                    px: 3,
                    py: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderBottom: i < displaySubscriptions.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                    },
                  }}
                >
                  {/* Avatar */}
                  <Avatar
                    src={avatar}
                    alt={username}
                    sx={{
                      width: 56,
                      height: 56,
                      border: `3px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  >
                    {!avatar && (name?.[0]?.toUpperCase() || '?')}
                  </Avatar>
                  
                  {/* Creator info */}
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem', mb: 0.25 }}>
                      {name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                      @{username}
                    </Typography>
                  </Box>
                  
                  {/* Review status/button */}
                  {!sub.reviewed ? (
                    <Button
                      variant="contained"
                      onClick={() => openReview(creator)}
                      sx={{
                        minWidth: 100,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      Review
                    </Button>
                  ) : (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Reviewed"
                      color="success"
                      sx={{
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.dark,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            color: 'text.secondary'
          }}>
            <Typography variant="h6" gutterBottom>
              No subscriptions found
            </Typography>
            <Typography variant="body2">
              Connect your OnlyFans account to see your subscriptions here.
            </Typography>
          </Box>
        )}
      </DialogContent>
      {/* Footer actions */}
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          startIcon={refreshingSubscriptions ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleRefreshSubscriptions}
          disabled={refreshingSubscriptions}
          sx={{
            flex: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            borderWidth: 2,
            py: 1.25,
            '&:hover': {
              borderWidth: 2,
            },
          }}
        >
          {refreshingSubscriptions ? 'Refreshing...' : 'Refresh'}
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            flex: 1,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            py: 1.25,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&:hover': {
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
